/**
 * WANLE-PRICE-1 · 上架中心 › 携程玩乐 › 「改价」抽屉（样稿 携程玩乐单产品固定价-样稿.html）。
 * 默认跟随售价中心的 API 渠道价；给这一行（产品选项）填一个固定金额，就只对携程玩乐生效，不影响 GYG / Viator / Bokun。
 * 填什么跟渠道中心「合同报价方式」走：推结算价 → 填结算价；推卖价 → 填卖价；另一个按全局佣金比例自动算。
 * 低于成本：红条 + 必填说明（同售价中心 ChannelOverrideDrawer 的固定金额交互）；成本 / 毛利只有「成本查看」权限才显示。
 *   PUT /api/v1/integrations/platforms/ctrip_wanle/mappings/{id}/wanle-price {fixed_cents|null, note?}
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "@/services/api";
import { wanleApi } from "@/services/ctripWanle";
import type { MappingProductRow, WanleBinding } from "@/services/types/channelCenter";

const usd2 = (c?: number | null) => (c == null ? "—" : c < 0 ? `-$${(-c / 100).toFixed(2)}` : `$${(c / 100).toFixed(2)}`);
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;
const dollars = (c: number | null | undefined) => (c == null ? "" : String(+(c / 100).toFixed(2)));

export function WanlePriceDrawer({ row, b, onClose, onSaved }: { row: MappingProductRow; b: WanleBinding; onClose: () => void; onSaved: () => void }) {
  const wp = b.wanle_price!;
  const mode = wp.contract_mode;                                   // cost = 推结算价 / sale = 推卖价 / null = 渠道中心还没选
  const comm = wp.commission_pct == null ? null : wp.commission_pct / 100;
  const [useFixed, setUseFixed] = useState(wp.source === "fixed");
  const [text, setText] = useState(wp.source === "fixed" ? dollars(wp.fixed_cents) : "");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ queued: boolean; willPush: boolean } | null>(null);

  const label = mode === "sale" ? "卖价" : "结算价";
  const t = text.trim();
  const textErr = useFixed && (!t ? "请填写金额" : !DECIMAL2.test(t) || Number(t) <= 0 ? "金额要大于 0，最多两位小数" : null);
  const cents = useFixed && !textErr ? Math.round(Number(t) * 100) : null;
  // 计算区：不管填的是结算还是卖价，另一个按全局佣金换算
  const settle = cents == null ? null : mode === "sale" ? (comm == null ? null : Math.round(cents * (1 - comm))) : cents;
  const sale = cents == null ? null : mode === "sale" ? cents : (comm == null ? null : Math.round(cents / (1 - comm)));
  const cost = wp.cost_cents;
  const below = settle != null && cost != null && settle < cost ? cost - settle : 0;
  const margin = settle != null && cost != null && settle > 0 ? { c: settle - cost, p: ((settle - cost) * 100) / settle } : null;
  const changed = useFixed ? cents !== wp.fixed_cents || mode !== wp.fixed_mode : wp.source === "fixed";
  const noteMissing = below > 0 && !note.trim();

  const save = useMutation({
    mutationFn: () => wanleApi.savePrice(b.mapping_id!, { fixed_cents: useFixed ? cents : null, note: note.trim() || undefined }),
    onSuccess: (r) => { setErr(null); setDone({ queued: r.queued, willPush: !!r.will_push_price }); onSaved(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "保存失败，请稍后再试"),
  });
  const canSave = changed && !save.isPending && !done && (!useFixed || (!textErr && mode != null)) && !noteMissing;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/35 backdrop-blur-[2px]" onClick={onClose} data-testid="wanle-price-drawer-bg">
      <aside className="flex h-full w-full max-w-[460px] flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="wanle-price-drawer" role="dialog" aria-label="携程玩乐价格">
        <div className="flex items-start justify-between gap-2.5 border-b border-slate-200 px-[18px] py-3.5">
          <div>
            <div className="text-[15px] font-semibold text-slate-900">携程玩乐价格 · {b.option_code}</div>
            <div className="text-[12px] text-slate-500">{row.product_code} {row.product_name}</div>
          </div>
          <button type="button" aria-label="关闭" onClick={onClose} className="text-[18px] leading-none text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-3.5">
          <label className={`flex cursor-pointer items-start gap-2.5 rounded-[10px] border px-3 py-2.5 ${!useFixed ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
            <input type="radio" className="mt-1 accent-blue-600" data-testid="wanle-price-follow" checked={!useFixed} onChange={() => setUseFixed(false)} />
            <div>
              <div className="text-[13px] font-semibold text-slate-900">跟随售价中心 API 渠道价（默认）</div>
              <div className="text-[12px] text-slate-500">现在 {usd2(wp.api_settle_cents)} / 人；售价中心改了这里跟着变</div>
            </div>
          </label>
          <label className={`flex items-start gap-2.5 rounded-[10px] border px-3 py-2.5 ${useFixed ? "border-blue-600 bg-blue-50" : "border-slate-200"} ${mode == null ? "opacity-60" : "cursor-pointer"}`}>
            <input type="radio" className="mt-1 accent-blue-600" data-testid="wanle-price-fixed" checked={useFixed} disabled={mode == null} onChange={() => setUseFixed(true)} />
            <div>
              <div className="text-[13px] font-semibold text-slate-900">给携程单独定固定价</div>
              {mode == null ? (
                <div className="text-[12px] text-rose-600">先在渠道中心「价格推送」选好合同报价方式，才能单独定价</div>
              ) : (
                <>
                  <div className="mt-1.5 flex items-center gap-1.5 text-[13px] text-slate-700">
                    {label} <span className="text-slate-500">$</span>
                    <input type="text" inputMode="decimal" aria-label={`固定${label}`} data-testid="wanle-price-input" value={text} disabled={!useFixed}
                      onChange={(e) => setText(e.target.value)} placeholder="—"
                      className={`h-7 w-[96px] rounded-[6px] border px-2 text-right text-[13px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500 ${textErr && useFixed ? "border-rose-400" : "border-slate-300"}`} />
                    / 人
                    {textErr && useFixed && <span className="text-[11px] text-rose-600">{textErr}</span>}
                  </div>
                  <div className="mt-1 text-[12px] text-slate-500">合同是「{mode === "sale" ? "推卖价" : "推结算价"}」，所以这里填{label}；合同若改「{mode === "sale" ? "推结算价" : "推卖价"}」，这一格自动变成填{mode === "sale" ? "结算价" : "卖价"}</div>
                </>
              )}
            </div>
          </label>

          {useFixed && mode != null && (
            <div className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1 rounded-lg bg-slate-50 px-3 py-2.5 text-[13px] tabular-nums" data-testid="wanle-price-calc">
              <span className="text-slate-500">推给携程</span>
              <span><b>{mode === "sale" ? `卖价 ${usd2(sale)}` : `结算价 ${usd2(settle)}`}</b></span>
              <span className="text-slate-500">{mode === "sale" ? "结算价（参考）" : "携程卖价（参考）"}</span>
              <span>
                {comm == null ? "佣金比例还没定，换算不出" : mode === "sale"
                  ? `${usd2(cents)} × (1 − ${+(comm * 100).toFixed(2)}%) = ${usd2(settle)}`
                  : `${usd2(cents)} ÷ (1 − ${+(comm * 100).toFixed(2)}%) = ${usd2(sale)}`}
              </span>
              {cost != null && <><span className="text-slate-500">我方成本</span><span data-testid="wanle-price-cost">{usd2(cost)}</span></>}
              {cost != null && <><span className="text-slate-500">毛利</span><span data-testid="wanle-price-margin">{margin ? `${usd2(margin.c)} · ${margin.p.toFixed(1)}%` : "—"}</span></>}
            </div>
          )}
          {wp.mode_changed && wp.source === "fixed" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700" data-testid="wanle-price-modechanged">合同报价方式已改，固定价按佣金换算显示。</div>
          )}
          {below > 0 && (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700" data-testid="wanle-price-below">低于成本 {usd2(below)} / 人：必须填写说明才能保存</div>
          )}
          <label className="block text-[12px] text-slate-500">改价说明（低于成本时必填；写进改价记录）
            <textarea aria-label="改价说明" data-testid="wanle-price-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="例：携程 11 月冲量活动" maxLength={500}
              className={`mt-1 min-h-[56px] w-full rounded-lg border px-2.5 py-1.5 text-[13px] text-slate-900 ${noteMissing ? "border-rose-400" : "border-slate-300"}`} />
          </label>
          <p className="m-0 text-[12px] text-slate-500">保存后立即推一次价格给携程（价格推送开关打开且配齐时）；改价记录同售价中心一处可查。</p>
          {err && <div role="alert" data-testid="wanle-price-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{err}</div>}
          {done && (
            <div data-testid="wanle-price-done" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800">
              已保存。{done.queued ? (done.willPush ? "已入队推价，约 1–2 分钟内推给携程。" : "已入队，但价格推送开关没开 / 没配齐 / 在沙箱模式，所以只推库存，价格暂不推。") : "没有需要推送的日期。"}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-[18px] py-3">
          <button type="button" onClick={onClose} className="h-[34px] rounded-lg border border-slate-200 px-3.5 text-[13px] text-slate-700 hover:bg-slate-50">{done ? "关闭" : "取消"}</button>
          {!done && <button type="button" data-testid="wanle-price-save" disabled={!canSave} onClick={() => save.mutate()} className="h-[34px] rounded-lg bg-blue-600 px-3.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{save.isPending ? "保存中…" : "保存"}</button>}
        </div>
      </aside>
    </div>
  );
}
